import {
  AccessTimeRounded as AccessTimeRoundedIcon,
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  BedtimeOutlined as BedtimeOutlinedIcon,
  FavoriteRounded as FavoriteRoundedIcon,
  HealingRounded as HealingRoundedIcon,
  LunchDiningOutlined as LunchDiningOutlinedIcon,
  MedicationRounded as MedicationRoundedIcon,
  PeopleOutlineRounded as PeopleOutlineRoundedIcon,
  RemoveRounded as RemoveRoundedIcon,
  WcOutlined as WcOutlinedIcon,
} from '@mui/icons-material';

export const CARE_LENS_KEYS = {
  behavior: 'behavior',
  dementia: 'dementia',
  chronicIllness: 'chronic_illness',
  hospice: 'hospice',
};

const BEHAVIOR_TRIGGER_GROUPS = [
  { key: 'medication_timing', label: 'Medication timing', icon: MedicationRoundedIcon },
  { key: 'food_dietary', label: 'Food / Dietary', icon: LunchDiningOutlinedIcon },
  { key: 'sleep_quality', label: 'Sleep quality', icon: BedtimeOutlinedIcon },
  { key: 'sensory_environment', label: 'Sensory overload', icon: AutoAwesomeOutlinedIcon },
  { key: 'transition', label: 'Transition', icon: AccessTimeRoundedIcon },
  { key: 'social', label: 'Social', icon: PeopleOutlineRoundedIcon },
  { key: 'other', label: 'Not sure', icon: RemoveRoundedIcon },
];

const COMFORT_TRIGGER_GROUPS = [
  { key: 'medication_timing', label: 'Medication timing', icon: MedicationRoundedIcon },
  { key: 'positioning', label: 'Positioning', icon: FavoriteRoundedIcon },
  { key: 'breathing', label: 'Breathing', icon: HealingRoundedIcon },
  { key: 'pain', label: 'Pain', icon: HealingRoundedIcon },
  { key: 'food_drink', label: 'Food / Drink', icon: LunchDiningOutlinedIcon },
  { key: 'bathroom', label: 'Bathroom', icon: WcOutlinedIcon },
  { key: 'skin_pressure', label: 'Skin / pressure', icon: HealingRoundedIcon },
  { key: 'anxiety', label: 'Anxiety', icon: PeopleOutlineRoundedIcon },
  { key: 'other', label: 'Not sure', icon: RemoveRoundedIcon },
];

const SYMPTOM_TRIGGER_GROUPS = [
  { key: 'medication_timing', label: 'Medication timing', icon: MedicationRoundedIcon },
  { key: 'food_drink', label: 'Food / Drink', icon: LunchDiningOutlinedIcon },
  { key: 'sleep_quality', label: 'Sleep quality', icon: BedtimeOutlinedIcon },
  { key: 'activity', label: 'Activity', icon: AccessTimeRoundedIcon },
  { key: 'pain', label: 'Pain', icon: HealingRoundedIcon },
  { key: 'bathroom', label: 'Bathroom', icon: WcOutlinedIcon },
  { key: 'other', label: 'Not sure', icon: RemoveRoundedIcon },
];

const DEMENTIA_TRIGGER_GROUPS = [
  { key: 'pain', label: 'Pain', icon: HealingRoundedIcon },
  { key: 'bathroom', label: 'Bathroom', icon: WcOutlinedIcon },
  { key: 'food_drink', label: 'Food / Drink', icon: LunchDiningOutlinedIcon },
  { key: 'sleep_quality', label: 'Sleep quality', icon: BedtimeOutlinedIcon },
  { key: 'environment', label: 'Noise / environment', icon: AutoAwesomeOutlinedIcon },
  { key: 'routine_change', label: 'Routine change', icon: AccessTimeRoundedIcon },
  { key: 'social', label: 'Social', icon: PeopleOutlineRoundedIcon },
  { key: 'other', label: 'Not sure', icon: RemoveRoundedIcon },
];

const buildSeverityPreset = ({ key, label, value, description, dot, selectedBg, text }) => ({
  key,
  label,
  value,
  description,
  dot,
  selectedBg,
  selectedBorder: selectedBg,
  text,
});

const STANDARD_SEVERITY_PRESETS = [
  buildSeverityPreset({
    key: 'low',
    label: 'Low',
    value: 2,
    description: 'Minor - handled easily',
    dot: '#6FA370',
    selectedBg: '#A8C89B',
    text: '#6FA370',
  }),
  buildSeverityPreset({
    key: 'moderate',
    label: 'Moderate',
    value: 5,
    description: 'Noticeable - needed support',
    dot: '#C68E3E',
    selectedBg: '#E8B464',
    text: '#C68E3E',
  }),
  buildSeverityPreset({
    key: 'high',
    label: 'High',
    value: 6,
    description: 'Significant - took time to calm',
    dot: '#C66E3E',
    selectedBg: '#E89A64',
    text: '#C66E3E',
  }),
  buildSeverityPreset({
    key: 'crisis',
    label: 'Crisis',
    value: 9,
    description: 'Safety concern - intervention',
    dot: '#B45050',
    selectedBg: '#D17A7A',
    text: '#B45050',
  }),
];

const COMFORT_SEVERITY_PRESETS = [
  buildSeverityPreset({
    key: 'comfortable',
    label: 'Comfortable',
    value: 2,
    description: 'Comfortable',
    dot: '#6FA370',
    selectedBg: '#A8C89B',
    text: '#6FA370',
  }),
  buildSeverityPreset({
    key: 'noticeable',
    label: 'Noticeable',
    value: 5,
    description: 'Needs comfort support',
    dot: '#C68E3E',
    selectedBg: '#E8B464',
    text: '#C68E3E',
  }),
  buildSeverityPreset({
    key: 'distressed',
    label: 'Distressed',
    value: 6,
    description: 'Hard to settle',
    dot: '#C66E3E',
    selectedBg: '#E89A64',
    text: '#C66E3E',
  }),
  buildSeverityPreset({
    key: 'urgent',
    label: 'Urgent',
    value: 9,
    description: 'Needs immediate attention',
    dot: '#B45050',
    selectedBg: '#D17A7A',
    text: '#B45050',
  }),
];

const SYMPTOM_SEVERITY_PRESETS = [
  { ...STANDARD_SEVERITY_PRESETS[0], label: 'Mild', description: 'Present - manageable' },
  { ...STANDARD_SEVERITY_PRESETS[1], label: 'Moderate', description: 'Noticeable - needs support' },
  { ...STANDARD_SEVERITY_PRESETS[2], label: 'Severe', description: 'Hard to manage' },
  { ...STANDARD_SEVERITY_PRESETS[3], label: 'Urgent', description: 'Needs immediate attention' },
];

const DEMENTIA_SEVERITY_PRESETS = [
  { ...STANDARD_SEVERITY_PRESETS[0], label: 'Calm', description: 'Settled - gentle support' },
  { ...STANDARD_SEVERITY_PRESETS[1], label: 'Noticeable', description: 'Change noticed' },
  { ...STANDARD_SEVERITY_PRESETS[2], label: 'Distressed', description: 'Needs steady support' },
  { ...STANDARD_SEVERITY_PRESETS[3], label: 'Unsafe', description: 'Safety concern' },
];

const ADAPTIVE_RESPONSE_CONFIGS = {
  [CARE_LENS_KEYS.behavior]: {
    lensKey: CARE_LENS_KEYS.behavior,
    chipLabel: 'Behavior',
    sheetTitle: 'Behavior',
    sheetSubtitle: "Log what happened. We'll pull in context so you don't have to.",
    inputLabel: 'What happened?',
    inputPlaceholder: 'Describe what happened, how long it lasted, and what helped.',
    severityLabel: 'Severity',
    severityPresets: STANDARD_SEVERITY_PRESETS,
    triggerTitle: 'What might have triggered this?',
    triggerHelper: 'Pick any that apply. You can also add a note.',
    triggerNotesLabel: 'Possible trigger',
    triggerNotesPlaceholder: 'Anything else to add?',
    triggerSummaryLabel: 'Possible context',
    triggerGroups: BEHAVIOR_TRIGGER_GROUPS,
    saveLabel: 'Save behavior',
    timelineDisplayLabel: 'Behavior',
  },
  [CARE_LENS_KEYS.dementia]: {
    lensKey: CARE_LENS_KEYS.dementia,
    chipLabel: 'Mood & Behavior',
    sheetTitle: 'Mood & Behavior',
    sheetSubtitle: 'Log the change and what helped them feel more settled.',
    inputLabel: 'What changed?',
    inputPlaceholder: 'Describe the mood, behavior, confusion, agitation, or comfort change you noticed.',
    severityLabel: 'Support level',
    severityPresets: DEMENTIA_SEVERITY_PRESETS,
    triggerTitle: 'What may have contributed?',
    triggerHelper: 'Pick any context that may help explain the change.',
    triggerNotesLabel: 'Context note',
    triggerNotesPlaceholder: 'Anything else that may have mattered?',
    triggerSummaryLabel: 'Related context',
    triggerGroups: DEMENTIA_TRIGGER_GROUPS,
    saveLabel: 'Save mood & behavior',
    timelineDisplayLabel: 'Mood & Behavior',
  },
  [CARE_LENS_KEYS.chronicIllness]: {
    lensKey: CARE_LENS_KEYS.chronicIllness,
    chipLabel: 'Symptoms',
    sheetTitle: 'Symptoms',
    sheetSubtitle: 'Log symptoms, changes, and what helped.',
    inputLabel: 'What symptoms or changes happened?',
    inputPlaceholder: 'Describe the symptom, how strong it was, how long it lasted, and what helped.',
    severityLabel: 'Severity',
    severityPresets: SYMPTOM_SEVERITY_PRESETS,
    triggerTitle: 'What might be connected?',
    triggerHelper: 'Pick any context that could help explain the symptom.',
    triggerNotesLabel: 'Related context',
    triggerNotesPlaceholder: 'Anything else to add?',
    triggerSummaryLabel: 'Related context',
    triggerGroups: SYMPTOM_TRIGGER_GROUPS,
    saveLabel: 'Save symptoms',
    timelineDisplayLabel: 'Symptoms',
  },
  [CARE_LENS_KEYS.hospice]: {
    lensKey: CARE_LENS_KEYS.hospice,
    chipLabel: 'Comfort',
    sheetTitle: 'Comfort',
    sheetSubtitle: 'Log comfort, symptoms, and what helped.',
    inputLabel: 'What comfort change happened?',
    inputPlaceholder: 'Describe comfort, pain, breathing, restlessness, positioning, or what helped.',
    severityLabel: 'Comfort level',
    severityPresets: COMFORT_SEVERITY_PRESETS,
    triggerTitle: 'What may be affecting comfort?',
    triggerHelper: 'Pick any context that may help the care circle understand.',
    triggerNotesLabel: 'Comfort context',
    triggerNotesPlaceholder: 'Anything else to add?',
    triggerSummaryLabel: 'Comfort context',
    triggerGroups: COMFORT_TRIGGER_GROUPS,
    saveLabel: 'Save comfort',
    timelineDisplayLabel: 'Comfort',
  },
};

const collectTextValues = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectTextValues);
  }

  if (typeof value === 'object') {
    return [
      value.code,
      value.label,
      value.name,
      value.value,
    ].filter(Boolean).flatMap(collectTextValues);
  }

  return [String(value).trim().toLowerCase()].filter(Boolean);
};

const collectCareFocusText = (child = {}) => {
  const values = [
    child?.careContext?.primaryCareFocus,
    child?.careContext?.primaryFocus,
    child?.careContext?.careFocus,
    child?.careContext?.additionalSupports,
    child?.careContext?.trackingFocus,
    child?.conditions,
    child?.concerns,
    child?.selectedConditions,
  ];

  return values.flatMap(collectTextValues).join(' ');
};

export const getAdaptiveCareLensKey = (child = {}) => {
  const careFocusText = collectCareFocusText(child);

  if (/\b(hospice|palliative|end of life|comfort care)\b/.test(careFocusText)) {
    return CARE_LENS_KEYS.hospice;
  }

  if (/\b(dementia|memory care|alzheimer|alzheimers|alzheimer's)\b/.test(careFocusText)) {
    return CARE_LENS_KEYS.dementia;
  }

  if (/\b(chronic illness|chronic condition|medical complexity|complex medical|symptom|symptoms)\b/.test(careFocusText)) {
    return CARE_LENS_KEYS.chronicIllness;
  }

  return CARE_LENS_KEYS.behavior;
};

export const getAdaptiveResponseQuickLogConfig = (child = {}) => (
  ADAPTIVE_RESPONSE_CONFIGS[getAdaptiveCareLensKey(child)] || ADAPTIVE_RESPONSE_CONFIGS[CARE_LENS_KEYS.behavior]
);

export const getAdaptiveResponseQuickLogConfigByLens = (lensKey = '') => (
  ADAPTIVE_RESPONSE_CONFIGS[lensKey] || ADAPTIVE_RESPONSE_CONFIGS[CARE_LENS_KEYS.behavior]
);

export const hasAdaptiveResponseEntryLens = (entry = {}) => Boolean(
  entry?.adaptiveQuickLogLabel
  || entry?.adaptiveQuickLogLens
  || entry?.incidentData?.adaptiveQuickLogLabel
  || entry?.incidentData?.adaptiveQuickLogLens
);

const getEntryResponseLensKey = (entry = {}) => (
  entry?.adaptiveQuickLogLens
  || entry?.incidentData?.adaptiveQuickLogLens
  || entry?.source?.adaptiveQuickLogLens
  || ''
);

export const resolveAdaptiveResponsePresentation = ({ entry = {}, child = null } = {}) => {
  const entryLensKey = getEntryResponseLensKey(entry);
  const config = entryLensKey
    ? getAdaptiveResponseQuickLogConfigByLens(entryLensKey)
    : getAdaptiveResponseQuickLogConfig(child || {});
  const categoryLabel = (
    entry?.adaptiveQuickLogLabel
    || entry?.incidentData?.adaptiveQuickLogLabel
    || entry?.incidentCategoryLabel
    || config.timelineDisplayLabel
    || config.chipLabel
    || 'Behavior'
  );

  return {
    lensKey: config.lensKey || entryLensKey || CARE_LENS_KEYS.behavior,
    categoryLabel,
    chipLabel: config.chipLabel || categoryLabel,
    title: `${categoryLabel} note`,
    severityLabel: config.severityLabel || 'Severity',
    severityPresets: config.severityPresets || STANDARD_SEVERITY_PRESETS,
    triggerNotesLabel: config.triggerNotesLabel || 'Possible trigger',
    triggerSummaryLabel: config.triggerSummaryLabel || 'Possible context',
  };
};

export const getAdaptiveResponseChipLabel = (child = {}) => (
  getAdaptiveResponseQuickLogConfig(child).chipLabel
);

export const getAdaptiveResponseTrackingTitle = (child = {}) => (
  getAdaptiveResponseQuickLogConfig(child).timelineDisplayLabel
);
